import { useCallback, useEffect, useRef, useState } from 'react'

type PendingDecision = {
  promise: Promise<boolean>
  resolve: (result: boolean) => void
}

export const useModalDecision = () => {
  const pendingRef = useRef<PendingDecision>()
  const [isOpen, setIsOpen] = useState(false)

  const settle = useCallback((result: boolean) => {
    const pending = pendingRef.current
    if (!pending) return

    pendingRef.current = undefined
    setIsOpen(false)
    pending.resolve(result)
  }, [])

  const requestDecision = useCallback((): Promise<boolean> => {
    if (pendingRef.current) {
      return pendingRef.current.promise
    }

    let resolveDecision!: (result: boolean) => void
    const promise = new Promise<boolean>((resolve) => {
      resolveDecision = resolve
    })

    pendingRef.current = { promise, resolve: resolveDecision }
    setIsOpen(true)
    return promise
  }, [])

  const confirm = useCallback(() => settle(true), [settle])
  const cancel = useCallback(() => settle(false), [settle])

  useEffect(
    () => () => {
      const pending = pendingRef.current
      if (!pending) return

      pendingRef.current = undefined
      pending.resolve(false)
    },
    []
  )

  return { isOpen, requestDecision, confirm, cancel }
}
